// src/pages/OrderHistoryPage.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './OrderHistoryPage.css';

/* --------------------------------- API base -------------------------------- */
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const apiUrl = (p) => (API_BASE ? `${API_BASE}${p}` : p);

/* --------------------------------- Helpers -------------------------------- */
const VAT_RATE = 0.255; // 25,5 %

const eur = (n) =>
  new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })
    .format(Number(n || 0) / 100);

const safeNum = (v, d = 0) => (Number.isFinite(v) ? v : d);

const computeItemsSubtotal = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((s, it) => {
    const qty = safeNum(Number(it.quantity), 1);
    const unit = Number.isFinite(it.unitPrice)
      ? it.unitPrice
      : Number.isFinite(it.price)
      ? it.price
      : 0;
    return s + qty * unit;
  }, 0);

/**
 * Palauttaa:
 *  - subtotal, discountAmount, deliveryFee
 *  - net  (veroton)  = subtotal - discount + delivery
 *  - vat  (ALV senteissä)
 *  - gross (sis. ALV) = net + vat
 * Hyödyntää so.vat/so.totalInclVat jos olemassa, muuten laskee ne.
 */
const computeSuborderTotals = (so = {}) => {
  const subtotal = Number.isFinite(so.subtotal) ? Number(so.subtotal) : computeItemsSubtotal(so.items);
  const discountAmount = safeNum(Number(so.discountAmount), 0);
  const deliveryFee = safeNum(Number(so.deliveryFee), 0);
  const net = Number.isFinite(so.total) ? Number(so.total) : Math.max(0, subtotal - discountAmount + deliveryFee);
  const vat = Number.isFinite(so.vat) ? Number(so.vat) : Math.round(net * VAT_RATE);
  const gross = Number.isFinite(so.totalInclVat) ? Number(so.totalInclVat) : net + vat;
  return { subtotal, discountAmount, deliveryFee, net, vat, gross };
};

const typeLabel = (so) => {
  const t = so?.deliveryType === 'immediate' ? 'normal' : 'economic';
  if (so?.origin === 'recurring' || so?.isRecurring) return 'recurring';
  return t; // 'normal' | 'economic'
};

const TYPE_LABELS = {
  normal: 'Normaalitilaus',
  economic: 'Säästötilaus',
  recurring: 'Toistuva tilaus',
};

const inferDiscountPercent = (so) => {
  const bd = so?.discountBreakdown || {};
  if (typeof bd.totalPercent === 'number') return bd.totalPercent; // 0|2|4
  const isImmediate = so?.deliveryType === 'immediate';
  const isRecurring = so?.origin === 'recurring' || so?.isRecurring;
  return isImmediate ? 0 : (isRecurring ? 4 : 2);
};

const formatDateTime = (ts) => {
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('fi-FI', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
    return `${date} klo ${time}`;
  } catch { return ''; }
};

/* ----------------------------- Small UI helpers --------------------------- */
function Chip({ children, variant }) {
  return (
    <span className={`oh-chip ${variant ? `oh-chip--${variant}` : ''}`}>
      {children}
    </span>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`oh-filter ${active ? 'oh-filter--active' : ''}`}
    >
      {children}
    </button>
  );
}

/* ================================ Component =============================== */
export default function OrderHistoryPage() {
  const navigate = useNavigate();
  const { user, isReady, getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState({});
  const [filter, setFilter] = useState('all'); // 'all' | 'normal' | 'economic' | 'recurring'

  // Redirect jos ei kirjautunut
  useEffect(() => {
    if (!isReady) return;
    if (!user) navigate('/login', { replace: true, state: { from: '/account/orders' } });
  }, [isReady, user, navigate]);

  // Hae toimittajat
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(apiUrl('/api/suppliers'));
        if (!alive) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach((s) => { if (s && s._id) map[String(s._id)] = s; });
        setSuppliers(map);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  // Hae tilaukset
  useEffect(() => {
    if (!isReady || !user) return;
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const { data } = await axios.get(apiUrl('/api/orders'), {
          headers: { ...getAuthHeaders() },
          params: { sort: '-createdAt', limit: 100 },
        });
        if (!alive) return;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : data?.orders || []);
        setOrders(list);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Tilausten haku epäonnistui.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isReady, user, getAuthHeaders]);

  const ordersSorted = useMemo(
    () => [...orders].sort((a, b) =>
      new Date(b?.createdAt || b?.created || 0) - new Date(a?.createdAt || a?.created || 0)
    ),
    [orders]
  );

  // Suodatus: pidä vain kortit, joissa on valittua tyyppiä olevia alitilauksia
  const filteredOrders = useMemo(() => {
    if (filter === 'all') return ordersSorted;
    return ordersSorted
      .map((o) => ({
        ...o,
        suborders: (Array.isArray(o?.suborders) ? o.suborders : []).filter((so) => typeLabel(so) === filter),
      }))
      .filter((o) => (Array.isArray(o.suborders) ? o.suborders.length > 0 : false));
  }, [ordersSorted, filter]);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h1 className="text-2xl md:text-3xl font-semibold">Tilaukset</h1>
        <div className="flex items-center gap-2 oh-gap-fallback">
          <FilterChip active={filter==='all'} onClick={() => setFilter('all')}>Kaikki</FilterChip>
          <FilterChip active={filter==='normal'} onClick={() => setFilter('normal')}>Normaalitilaukset</FilterChip>
          <FilterChip active={filter==='economic'} onClick={() => setFilter('economic')}>Säästötilaukset</FilterChip>
          <FilterChip active={filter==='recurring'} onClick={() => setFilter('recurring')}>Toistuvat</FilterChip>
        </div>
      </div>

      {loading && <div className="text-sm opacity-80">Ladataan tilaushistoriaa…</div>}
      {!loading && error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">{error}</div>
      )}
      {!loading && !error && filteredOrders.length === 0 && (
        <div className="text-sm opacity-80">Ei tilauksia suodatuksella.</div>
      )}

      {!loading && !error && filteredOrders.length > 0 && (
        <div className="space-y-6">
          {filteredOrders.map((o) => (
            <OrderCard key={o._id} order={o} suppliers={suppliers} />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Subviews ------------------------------- */
function OrderCard({ order, suppliers }) {
  const createdAt = order?.createdAt || order?.created;
  const suborders = useMemo(
    () => (Array.isArray(order?.suborders) ? order.suborders : []),
    [order?.suborders]
  );

  // Lasketaan yhteen vain kortissa näkyvät alitilaukset (ALV-erittelyllä)
  const totals = useMemo(() => {
    const aggFromSubs = suborders.reduce((acc, so) => {
      const t = computeSuborderTotals(so);
      acc.subtotal += t.subtotal;
      acc.discount += t.discountAmount;
      acc.delivery += t.deliveryFee;
      acc.net += t.net;
      acc.vat += t.vat;
      acc.gross += t.gross;
      return acc;
    }, { subtotal: 0, discount: 0, delivery: 0, net: 0, vat: 0, gross: 0 });

    // Jos backend on jo laskenut, käytä sitä totuutena
    if (Number.isFinite(order?.totalAmount)) {
      const gross = Number(order.totalAmount);
      const vat = Number.isFinite(order?.vatAmount) ? Number(order.vatAmount) : aggFromSubs.vat;
      const net = gross - vat;
      return { ...aggFromSubs, net, vat, gross };
    }

    return aggFromSubs;
  }, [order?.totalAmount, order?.vatAmount, suborders]);

  const orderNumber =
    typeof order?.orderNumber !== 'undefined' && order?.orderNumber !== null
      ? `#${order.orderNumber}`
      : (order?._id ? `#${String(order._id).slice(-6).toUpperCase()}` : '—');

  return (
    <div className="oh-card">
      {/* Otsikko: tilausnumero ja päiväys chippeinä, summa oikealle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap oh-gap-fallback">
          <Chip>{orderNumber}</Chip>
          <Chip>{formatDateTime(createdAt)}</Chip>
        </div>
        <div className="text-lg font-semibold">{eur(totals.gross)}</div>
      </div>

      {/* Metatiedot */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2 text-sm">
        <div>
          <div className="oh-muted">Tilausnumero</div>
          <div className="font-medium">{order?.orderNumber ?? orderNumber}</div>
        </div>
        <div>
          <div className="oh-muted">Päiväys</div>
          <div className="font-medium">{formatDateTime(createdAt)}</div>
        </div>
        <div>
          <div className="oh-muted">Yhteensä</div>
          <div className="font-semibold">{eur(totals.gross)}</div>
        </div>
      </div>

      {/* Alitilaukset */}
      <div className="space-y-4 mt-2">
        {suborders.map((so, i) => (
          <SuborderBlock key={so._id || i} so={so} suppliers={suppliers} />
        ))}
      </div>

      <div className="mt-4">
        <Link
          to={`/order/${order._id}`}
          className="inline-flex items-center h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
        >
          Näytä tarkemmat tiedot
        </Link>
      </div>
    </div>
  );
}

function SuborderBlock({ so, suppliers }) {
  const kind = typeLabel(so);
  const label = TYPE_LABELS[kind] || 'Tilaus';

  const supplierName =
    so.supplierName || so.supplier?.displayName || suppliers?.[String(so.supplierId)]?.displayName ||
    so.supplier?.name || suppliers?.[String(so.supplierId)]?.name || 'Toimittaja';

  const { subtotal, discountAmount, deliveryFee, net, vat, gross } = computeSuborderTotals(so);
  const pct = inferDiscountPercent(so);

  return (
    <div className="oh-subcard">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap oh-gap-fallback">
          <Chip variant={kind}>{label}</Chip>
          <Chip variant="supplier">{supplierName}</Chip>
        </div>
        <div className="font-semibold">{eur(gross)}</div>
      </div>

      {/* Tuoterivit */}
      <div className="oh-lines">
        {(Array.isArray(so.items) ? so.items : []).map((it, i) => {
          const qty = safeNum(Number(it.quantity), 1);
          const unit = Number.isFinite(it.unitPrice) ? it.unitPrice : Number.isFinite(it.price) ? it.price : 0;
          const line = qty * unit;
          return (
            <div key={i} className="py-3 px-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium truncate">{it.name || it.title || 'Tuote'}</div>
                <div className="text-sm oh-muted">{qty} × {eur(unit)}</div>
              </div>
              <div className="font-medium whitespace-nowrap">{eur(line)}</div>
            </div>
          );
        })}
      </div>

      {/* Yhteenveto */}
      <div className="oh-summary">
        <Row label="Välisumma" value={subtotal} />
        {discountAmount > 0 && (
          <Row label={`Alennukset${pct ? ` (−${pct} %)` : ''}`} value={-discountAmount} dim />
        )}
        <Row label="Toimituskulut" value={deliveryFee} dim />
        <div className="h-px" style={{ background: 'var(--oh-border)', margin: '8px 0' }} />
        <Row label="Yhteensä (veroton)" value={net} />
        <Row label="ALV 25,5 %" value={vat} />
        <Row label={`Yhteensä (sis. ALV) (${supplierName})`} value={gross} strong />
      </div>
    </div>
  );
}

function Row({ label, value, strong, dim }) {
  return (
    <div className={`flex items-center justify-between gap-4 ${strong ? 'font-semibold' : 'font-medium'} ${dim ? 'opacity-80' : ''}`}>
      <div>{label}</div>
      <div>{eur(value)}</div>
    </div>
  );
}
